import TextBox from "../../../elements/textbox";
import i18n from '../../../../../utils/translations/i18n';
import {useEffect, useRef, useState} from "react";
import { mobileAndTabletDetectionForWidthLessThan1024 } from "../../../../../utils/browser";
import Lineitem from "../../../../../models/lineitem";
import { useGetInventoryDetailsQuery } from "../../../../../store/inventory";
import './multiattribute.scss';
import { getCartItemUserSelections } from "./utils";
import { getLocalizedSizeValue } from "../../../../../utils/cart";
import { config } from "../../../../../utils/config";

type MultiAttributeProps = {
  isShoppingFeed: boolean;
  multiDisplayDataObj: any;
  tierPricesData: any;
  handleChange: any;
  handleBlur: any;
  resMap: any;
  minQuantityCondition: any;
  noinputError: any;
  setIsUpdateDisable: any;
  productKey: String;
  mainItem?: Lineitem;
};

export const MultiSelectForm = ({ isShoppingFeed, multiDisplayDataObj, tierPricesData, handleChange, handleBlur, resMap, minQuantityCondition, noinputError, setIsUpdateDisable, productKey, mainItem }: MultiAttributeProps) => {
  const MULTISELECT_MAXLENGTH = 5;
  const multiAttrKeys = Object.keys(multiDisplayDataObj);
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomGradientRef = useRef<HTMLDivElement>(null);
  const { t } = i18n;
  const keysToRemove = ["Size", "Product Size"];
  const tempSelection = JSON.parse(JSON.stringify(getCartItemUserSelections(mainItem)));
  const filteredSelections = Object.fromEntries(
    Object.entries(tempSelection).filter(([key]) => !keysToRemove.includes(key))
  );
  const { data: inventoryData, isLoading} = useGetInventoryDetailsQuery({productKey: productKey, country: config('regionCode'), filteredSelections});
  const [sizeAvailabilityMap, setSizeAvailabilityMap] = useState({});

  useEffect(()=>{
    if(inventoryData){
      let availabilityMap = {};
      inventoryData.combinations.forEach(varient => {
        const size = varient.selectableAttributes.Size || varient.selectableAttributes['Product Size'];
        availabilityMap[getLocalizedSizeValue(size, mainItem?.productSpecification?.selectableAttributes)] = varient.status;
      });
      setSizeAvailabilityMap(availabilityMap);
    }
  },[inventoryData, mainItem?.productSpecification?.selectableAttributes])

  useEffect(()=>{
    if(!isLoading) setIsUpdateDisable(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isLoading]);


  useEffect(() => {
    const container = containerRef.current;
    const bottomGradient = bottomGradientRef.current;
    if (!container || !bottomGradient) {
      return;
    }

    const handleScroll = () => {
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Check if we are at the bottom of the container
    const buffer = 32;
    const atBottom = scrollTop + clientHeight >= scrollHeight - buffer;

        if (atBottom) {
            bottomGradient.style.display = 'none';
        } else {
            bottomGradient.style.display = 'block';
        }
    }

    container.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  },[]);
  
  const renderUnitPriceV2 = (tierPricesData, isShoppingFeed, index, tierData) => {
    if (tierPricesData?.isStrikeOutPricing) {
      return  (<td key={tierData?.quantity} className="price-row">
                <div className="line-through edit-quantity-modal-td"> {tierPricesData?.oldPrices[index]?.unitPrice} </div>
                <div className="edit-quantity-modal-td new-unit-price-multi-select-edit-modal"> {tierData?.unitPrice} </div>
              </td>);
    } else if (isShoppingFeed) {
      return <td key={tierData?.quantity} className="price-row"><div  className='edit-quantity-modal-td' > {tierData?.incTaxUnitPriceWithCurrency}</div ></td>
    } else {
      let quantity = tierData?.quantity;
      let unitQuantity = "unit-price-" + quantity + " edit-quantity-modal-td";
      return <td key={tierData?.quantity} className="price-row"><div  className={unitQuantity} > {tierData?.unitPrice}</div ></td>
    }
  }

  const renderUnitPriceV2Mobile = (tierPricesData, isShoppingFeed, index, tierData) => {
    if (tierPricesData?.isStrikeOutPricing) {
      return  (<td key={tierData?.quantity} className="mobile-unit-price-td"><span className="line-through"> {tierPricesData?.oldPrices[index]?.unitPrice} </span><span className="new-unit-price-multi-select-edit-modal"> {tierData?.unitPrice} </span></td>);
    } else if (isShoppingFeed) {
      return <td key={tierData?.quantity} className="mobile-unit-price-td"><span> {tierData?.incTaxUnitPriceWithCurrency}</span ></td>
    } else {
      let quantity = tierData?.quantity;
      let unitQuantity = "unit-price-" + quantity;
      return <td key={quantity} className="mobile-unit-price-td"><span className={unitQuantity}>{tierData?.unitPrice}</span></td>
    }
  }

  const renderDesktopTable = () => (
    <div className="pricing-table-product-details-multi-select">
      <table className="multi-select-desktop-table">
        <tbody>
          <tr>
            <td className="quantity-row">
              <div className="edit-quantity-modal-th">{t('product_details.quantity')}</div>
            </td>
            {tierPricesData.tierObject.map((tierData) => (
              <td key={tierData?.quantity} className="quantity-row">
                <div className="edit-quantity-modal-td">{tierData?.quantity}+</div>
              </td>
            ))}
          </tr>
        </tbody>
        <tbody>
          <tr>
            <td className="price-row">
              <div className="edit-quantity-modal-th">{t('product_details.unit_price')}</div>
            </td>
            {tierPricesData.tierObject.map((tierData, index) => (
              renderUnitPriceV2(tierPricesData, isShoppingFeed, index, tierData)
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderMobileTable = () => (
    <table className="multi-select-mobile-table">
      <thead>
        <tr>
          <th className="mobile-quantity-th">{t('product_details.quantity')}</th>
          <th className="mobile-unit-price-th">{t('product_details.unit_price')}</th>
        </tr>
      </thead>
      <tbody>
        {tierPricesData.tierObject.map((tierData, index) => (
          <tr key={index}>
            <td className="mobile-quantity-td">{tierData?.quantity}+</td>
            {renderUnitPriceV2Mobile(tierPricesData, isShoppingFeed, index, tierData)}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const handleGaEvent = () => {
    handleBlur();
  };

  const handleMultiValueChange = (event) => {
    if (!isNaN(Number(event.target.value))) {
      if (event.target.value.length > MULTISELECT_MAXLENGTH) {
        event.target.value = event.target.value.slice(0, MULTISELECT_MAXLENGTH);
      } else {
        handleChange(event.target.id, Number(event.target.value));
      }
    }
  }

  const onKeyDown = (e) => {
    const isStandardNumberKey = e.which >= 48 && e.which <= 57; // Numbers 0-9 on standard keyboard
    const isNumpadNumberKey = e.which >= 96 && e.which <= 105; // Numbers 0-9 on numpad
    const isControlKey = [8, 9, 37, 38, 39, 40, 46].includes(e.which); // Backspace, Tab, Arrow keys, and Delete
   
    // Allow only number keys and control keys, prevent all others
    if (!isStandardNumberKey && !isNumpadNumberKey && !isControlKey) {
      e.preventDefault();
    }
  };

  const multiSelectAttributePart = <>
    <div className="clothing__multiple__size multiSelectQuantityParentDiv">
      <div className="row edit-quantity-row">
        {
          multiAttrKeys.map((key) => {
            const attribute = multiDisplayDataObj[key];
            return (
              <div className="col-xs-6 col-sm-4 col-lg-2 multiSelectQuantityDiv-width" key={key}>
                <div className="multiSelectQuantityDiv">
                  {sizeAvailabilityMap[attribute.attributeValue] === 'LONG LEAD TIME' ?
                    <TextBox id={key} placeholder='0' disabled label={(attribute.attributeValue).toUpperCase()} type="number" min={0} pattern="[0-9]*" value={attribute.qty === 0 ? '' : attribute.qty} onChange={handleMultiValueChange} className='multiSelectInput disabled isOutOfStock' onKeyDown={onKeyDown} labelClass='label-size' /> :
                    <>
                      {resMap[(attribute.attributeValue).toUpperCase()] && <TextBox id={key} placeholder='0' disabled label={(attribute.attributeValue).toUpperCase()} type="number" min={0} pattern="[0-9]*" value={attribute.qty === 0 ? '' : attribute.qty} onChange={handleMultiValueChange} className='multiSelectInput disabled' onKeyDown={onKeyDown} labelClass='label-size' />}
                      {!resMap[(attribute.attributeValue).toUpperCase()] && <TextBox id={key} placeholder='0' label={(attribute.attributeValue).toUpperCase()} type="number" min={0} pattern="[0-9]*" value={attribute.qty === 0 ? '' : attribute.qty} onChange={handleMultiValueChange} onBlur={handleGaEvent} className='multiSelectInput' onKeyDown={onKeyDown} labelClass='label-size' />}
                    </>
                  }
                </div>
              </div>
            );
          })}
      </div>
    </div>
    {(minQuantityCondition && !noinputError) && <span className="error multi-select-error">{t('product_details.min_items_msg_1st_half')}{tierPricesData.tierObject[0].quantity}</span>}
    {noinputError && <span className="error multi-select-error">{t('product_details.no_value_entered')}</span>}
    <div>
      {mobileAndTabletDetectionForWidthLessThan1024() ? renderMobileTable() : renderDesktopTable()}
    </div>
  </>;

  return (
    <>
    <div ref={containerRef} className="mobile-sizes-pricing-container">
      {multiSelectAttributePart}
      <div ref={bottomGradientRef} className="bottom-gradient"></div>
    </div>  
      {tierPricesData.tierObject.length === 0 && <div className="highlight"> {t('product_details.no_tier_price_avl')}</div>}
      <div className="min-saving-percentage-container">
        <span>{t('product_details.min_saving_percentage1')}</span>
        <span className="min-saving-percentage-value"></span>
        <span>{t('product_details.min_saving_percentage2')}</span>
      </div>
    </>
  );
}

